'use client'

import { useTransition } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { ForgotPasswordInput, forgotPasswordSchema } from '@/schemas/auth'
import { Loader2 } from 'lucide-react'
import { forgotPasswordAction } from '@/app/lib/actions/auth'
import { toast } from "sonner"
import Link from 'next/link'
import { SlideUp } from '@/components/slide-up'

export function ForgotPasswordForm() {
    const [isPending, startTransition] = useTransition()
    const form = useForm<ForgotPasswordInput>({
        resolver: zodResolver(forgotPasswordSchema),
        defaultValues: {
            email: '',
        },
    })

    async function onSubmit(data: ForgotPasswordInput) {
        startTransition(async () => {
            const formData = new FormData()
            Object.entries(data).forEach(([key, value]) => {
                formData.append(key, value as string)
            })

            const result = await forgotPasswordAction(formData)
            if (result.success) {
                toast.success('Reset link has been sent to your email')
            } else {
                toast.error('Failed to send reset link')
            }

        })
    }


    return (
        <SlideUp>
            <div className="w-full max-w-md mx-auto">
                <h2 className="mb-2 text-3xl font-bold">Forgot Password</h2>
                <p className="mb-8 text-[#4b4b4b]">Enter your email to reset your password.</p>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                        <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Email</FormLabel>
                                    <FormControl>
                                        <Input
                                            type="email"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />


                        <Button type="submit" className="w-full" disabled={isPending}>
                            {isPending ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Sending reset link...
                                </>
                            ) : (
                                "Reset password"
                            )}
                        </Button>
                    </form>
                </Form>
                <p className="mt-6 text-center text-[#4b4b4b]">
                    Back to{" "}
                    <Link href="/login" className=" text-primary hover:underline">
                        Login
                    </Link>
                </p>
            </div>
        </SlideUp>
    )
} 